import { ArrowLeft, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsTresorerie, useComptesTresorerie, useDossiersTresorerie, useOperationTresorerie, useOperationsTresorerie, type OperationSaisie } from '@/api/hooks';
import { LIBELLE_TYPE_OPERATION, type DetailsNivellement, type DetailsRetourCaisse, type ResumeOperation, type TypeOperation } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader } from '@/components/common';
import { HistoriqueListe } from '@/components/courrier';
import { OngletsTresorerie, StatutOperationBadge, TableOperations, aujourdhui, estResponsableTresorerie, estTresorier } from '@/components/tresorerie';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Textarea } from '@/components/ui/textarea';
import { dateFr, dateHeureFr, montant } from '@/lib/utils';

const message = (e: unknown) => (e instanceof Error ? e.message : 'Action impossible.');

function DialogueOperation({ ouvert, onOpenChange }: { ouvert: boolean; onOpenChange: (o: boolean) => void }) {
  const actions = useActionsTresorerie();
  const comptes = useComptesTresorerie();
  const payes = useDossiersTresorerie({ statut: 'PAYE' });
  const rapproches = useDossiersTresorerie({ statut: 'RAPPROCHE' });
  const [v, setV] = useState<OperationSaisie>({ type: 'RETOUR_CAISSE', montant: 0, motif: '', lieu: 'CAISSE', dateRetour: aujourdhui() });
  const maj = (patch: Partial<OperationSaisie>) => setV({ ...v, ...patch });
  const banques = (comptes.data ?? []).filter((c) => c.type === 'BANQUE' && c.actif);
  const feds = [...(payes.data ?? []), ...(rapproches.data ?? [])];
  const creer = async () => {
    try {
      const corps: OperationSaisie = v.type === 'RETOUR_CAISSE' ? { type: v.type, montant: v.montant, motif: v.motif.trim(), fedOrigineId: v.fedOrigineId, deposant: v.deposant?.trim() || undefined, dateRetour: v.dateRetour || undefined, lieu: v.lieu, numeroRecu: v.numeroRecu?.trim() || undefined, compteCrediteId: v.lieu === 'BANQUE' ? v.compteCrediteId : undefined } : { type: v.type, montant: v.montant, motif: v.motif.trim(), compteDebiteId: v.compteDebiteId, compteCrediteId: v.compteCrediteId, dateSouhaitee: v.dateSouhaitee || undefined };
      const o = await actions.creerOperation.mutateAsync(corps);
      toast.success(`${LIBELLE_TYPE_OPERATION[o.type]} ${o.numero} émis.`);
      onOpenChange(false);
    } catch (e) {
      toast.error(message(e));
    }
  };
  const valide = v.montant > 0 && v.motif.trim().length >= 5 && (v.type === 'RETOUR_CAISSE' ? Boolean(v.fedOrigineId) && (v.lieu !== 'BANQUE' || Boolean(v.compteCrediteId)) : Boolean(v.compteDebiteId && v.compteCrediteId && v.compteDebiteId !== v.compteCrediteId));
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Nouvelle opération de trésorerie</DialogTitle>
          <DialogDescription>Retour de caisse (fonds non dépensés restitués après un FED payé) ou nivellement entre deux comptes du Fonds. Signatures selon le montant : DF, puis DGA Finances et DG aux mêmes seuils que les FED.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="typeOp">Type</Label>
            <SelectNatif id="typeOp" value={v.type} onChange={(e) => maj({ type: e.target.value as TypeOperation })}>
              <option value="RETOUR_CAISSE">Retour de caisse</option>
              <option value="NIVELLEMENT">Nivellement entre comptes</option>
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="montantOp">Montant (USD)</Label>
            <Input id="montantOp" type="number" min={0} step="0.01" value={v.montant || ''} onChange={(e) => maj({ montant: Number(e.target.value) })} />
          </div>
          {v.type === 'RETOUR_CAISSE' ? (
            <>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="fedOrigine">FED d'origine (payé)</Label>
                <SelectNatif id="fedOrigine" value={v.fedOrigineId ?? ''} onChange={(e) => maj({ fedOrigineId: e.target.value || undefined })}>
                  <option value="">Choisir</option>
                  {feds.map((d) => (
                    <option key={d.id} value={d.fed.id}>
                      {d.fed.numero} · {d.fed.objet.slice(0, 50)} · {montant(d.fed.montantUsd, true)}
                    </option>
                  ))}
                </SelectNatif>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="deposant">Déposant</Label>
                <Input id="deposant" value={v.deposant ?? ''} onChange={(e) => maj({ deposant: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dateRetour">Date du retour</Label>
                <Input id="dateRetour" type="date" value={v.dateRetour ?? ''} onChange={(e) => maj({ dateRetour: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lieu">Fonds reçus</Label>
                <SelectNatif id="lieu" value={v.lieu} onChange={(e) => maj({ lieu: e.target.value as 'CAISSE' | 'BANQUE', compteCrediteId: undefined })}>
                  <option value="CAISSE">En caisse</option>
                  <option value="BANQUE">Versés en banque</option>
                </SelectNatif>
              </div>
              {v.lieu === 'BANQUE' ? (
                <div className="space-y-1.5">
                  <Label htmlFor="compteReception">Compte de réception</Label>
                  <SelectNatif id="compteReception" value={v.compteCrediteId ?? ''} onChange={(e) => maj({ compteCrediteId: e.target.value || undefined })}>
                    <option value="">Choisir</option>
                    {banques.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.banque} · {c.code}
                      </option>
                    ))}
                  </SelectNatif>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="numeroRecu">N° de reçu de caisse</Label>
                  <Input id="numeroRecu" value={v.numeroRecu ?? ''} onChange={(e) => maj({ numeroRecu: e.target.value })} />
                </div>
              )}
              {v.lieu === 'BANQUE' && (
                <div className="space-y-1.5">
                  <Label htmlFor="numeroBordereau">N° de bordereau de versement</Label>
                  <Input id="numeroBordereau" value={v.numeroRecu ?? ''} onChange={(e) => maj({ numeroRecu: e.target.value })} />
                </div>
              )}
            </>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="debite">Compte débité</Label>
                <SelectNatif id="debite" value={v.compteDebiteId ?? ''} onChange={(e) => maj({ compteDebiteId: e.target.value || undefined })}>
                  <option value="">Choisir</option>
                  {banques.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.banque} · {c.code} · disponible {montant(c.soldeDisponible)}
                    </option>
                  ))}
                </SelectNatif>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="credite">Compte crédité</Label>
                <SelectNatif id="credite" value={v.compteCrediteId ?? ''} onChange={(e) => maj({ compteCrediteId: e.target.value || undefined })}>
                  <option value="">Choisir</option>
                  {(comptes.data ?? [])
                    .filter((c) => c.actif)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.banque} · {c.code}
                        {c.type === 'CAISSE' ? ' (caisse)' : ''}
                      </option>
                    ))}
                </SelectNatif>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dateSouhaitee">Date souhaitée (facultatif)</Label>
                <Input id="dateSouhaitee" type="date" value={v.dateSouhaitee ?? ''} onChange={(e) => maj({ dateSouhaitee: e.target.value })} />
              </div>
            </>
          )}
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="motifOp">Motif</Label>
            <Textarea id="motifOp" rows={2} value={v.motif} onChange={(e) => maj({ motif: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={creer} disabled={!valide || actions.creerOperation.isPending}>
            Émettre
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Retours de caisse et nivellements (§ 10) : liste et émission. */
export function OperationsTresorerie() {
  const session = useSession();
  const profils = session?.utilisateur.profils ?? [];
  const { data, isLoading, error } = useOperationsTresorerie();
  const [creation, setCreation] = useState(false);
  const peutEmettre = estResponsableTresorerie(profils) || estTresorier(profils);
  const enAttente = (data ?? []).filter((o) => o.actions.viser || o.actions.decider || o.actions.executer);
  return (
    <div>
      <PageHeader
        titre="Retours de caisse et nivellements"
        sousTitre="FED de trésorerie : retours de fonds non dépensés et mouvements entre comptes du Fonds."
        actions={
          peutEmettre && (
            <Button onClick={() => setCreation(true)}>
              <Plus className="size-4" /> Opération
            </Button>
          )
        }
      />
      <OngletsTresorerie />
      {isLoading ? (
        <Chargement />
      ) : error || !data ? (
        <ErreurBloc erreur={error} />
      ) : (
        <div className="space-y-6">
          {enAttente.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>À traiter par vous ({enAttente.length})</CardTitle>
                <CardDescription>Visa, décision ou exécution attendus.</CardDescription>
              </CardHeader>
              <CardContent>
                <TableOperations operations={enAttente} />
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader>
              <CardTitle>Toutes les opérations ({data.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <TableOperations operations={data} vide="Aucune opération de trésorerie." />
            </CardContent>
          </Card>
        </div>
      )}
      {creation && <DialogueOperation ouvert onOpenChange={(o) => !o && setCreation(false)} />}
    </div>
  );
}

function DialogueExecution({ o, ouvert, onOpenChange }: { o: ResumeOperation; ouvert: boolean; onOpenChange: (ouvert: boolean) => void }) {
  const actions = useActionsTresorerie();
  const [date, setDate] = useState(aujourdhui());
  const [reference, setReference] = useState('');
  const executer = async () => {
    try {
      await actions.executerOperation.mutateAsync({ id: o.id, dateExecution: date, reference: reference.trim() });
      toast.success('Nivellement exécuté : comptes mouvementés.');
      onOpenChange(false);
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Exécuter le nivellement {o.numero}</DialogTitle>
          <DialogDescription>
            {montant(o.montant, true)} de {o.compteDebite?.code} vers {o.compteCredite?.code}. Référence du virement sur la plateforme bancaire.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="dateExec">Date d'exécution</Label>
            <Input id="dateExec" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="refExec">Référence</Label>
            <Input id="refExec" value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={executer} disabled={actions.executerOperation.isPending || !date || reference.trim().length < 3}>
            Exécuter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DialogueDecision({ o, ouvert, onOpenChange }: { o: ResumeOperation; ouvert: boolean; onOpenChange: (ouvert: boolean) => void }) {
  const actions = useActionsTresorerie();
  const [decision, setDecision] = useState<'VALIDER' | 'REJETER'>('VALIDER');
  const [motif, setMotif] = useState('');
  const decider = async () => {
    try {
      const r = await actions.deciderOperation.mutateAsync({ id: o.id, decision, motif: motif.trim() || undefined });
      toast.success(decision === 'VALIDER' ? `Validé : ${r.libelleStatut.toLowerCase()}.` : 'Opération rejetée.');
      onOpenChange(false);
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Décision · {o.numero}</DialogTitle>
          <DialogDescription>{o.libelleStatut}. Votre décision est signée électroniquement et journalisée.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant={decision === 'VALIDER' ? 'default' : 'outline'} onClick={() => setDecision('VALIDER')}>
            Valider
          </Button>
          <Button type="button" size="sm" variant={decision === 'REJETER' ? 'default' : 'outline'} onClick={() => setDecision('REJETER')}>
            Rejeter
          </Button>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="motifOpDecision">{decision === 'REJETER' ? 'Motif du rejet' : 'Commentaire (facultatif)'}</Label>
          <Textarea id="motifOpDecision" rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button variant={decision === 'REJETER' ? 'destructive' : 'default'} onClick={decider} disabled={actions.deciderOperation.isPending || (decision === 'REJETER' && motif.trim().length < 5)}>
            Confirmer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Détail d'une opération de trésorerie : visa, décisions, exécution, historique. */
export function OperationDetail() {
  const { id } = useParams();
  const { data: o, isLoading, error } = useOperationTresorerie(id);
  const actions = useActionsTresorerie();
  const [dialogue, setDialogue] = useState<'decision' | 'execution' | null>(null);
  if (isLoading) return <Chargement />;
  if (error || !o) return <ErreurBloc erreur={error} />;
  const viser = async () => {
    try {
      await actions.viserOperation.mutateAsync(o.id);
      toast.success('Opération visée : transmise au Directeur financier.');
    } catch (e) {
      toast.error(message(e));
    }
  };
  const rc = o.type === 'RETOUR_CAISSE' ? (o.details as DetailsRetourCaisse) : null;
  const nv = o.type === 'NIVELLEMENT' ? (o.details as DetailsNivellement) : null;
  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to="/tresorerie/operations" className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Retours et nivellements
          </Link>
        }
        titre={
          <span className="flex flex-wrap items-center gap-2">
            {o.numero} <StatutOperationBadge o={o} />
          </span>
        }
        sousTitre={`${LIBELLE_TYPE_OPERATION[o.type]} · ${montant(o.montant, true)} · émis par ${o.emetteur?.nom ?? '—'} le ${dateHeureFr(o.createdAt)}`}
        actions={
          <>
            {o.actions.viser && (
              <Button onClick={viser} disabled={actions.viserOperation.isPending}>
                Viser
              </Button>
            )}
            {o.actions.decider && <Button onClick={() => setDialogue('decision')}>Décider</Button>}
            {o.actions.executer && <Button onClick={() => setDialogue('execution')}>Exécuter</Button>}
          </>
        }
      />
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Détail</CardTitle>
            <CardDescription>{o.motif}</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              {rc && (
                <>
                  <div>
                    <dt className="text-xs text-muted-foreground">FED d'origine</dt>
                    <dd>
                      {o.fedOrigine ? (
                        <Link to={`/fed/${o.fedOrigine.id}`} className="text-primary hover:underline">
                          {o.fedOrigine.numero} · {o.fedOrigine.objet} · {montant(o.fedOrigine.montantUsd, true)}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Déposant · date</dt>
                    <dd>
                      {rc.deposant} · {dateFr(rc.dateRetour)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Fonds reçus</dt>
                    <dd>
                      {rc.lieu === 'CAISSE' ? 'en caisse' : `en banque · ${o.compteCredite?.banque ?? ''} ${o.compteCredite?.code ?? ''}`} · {rc.lieu === 'CAISSE' ? 'reçu' : 'bordereau'} n° {rc.numeroRecu}
                    </dd>
                  </div>
                </>
              )}
              {nv && (
                <>
                  <div>
                    <dt className="text-xs text-muted-foreground">Compte débité</dt>
                    <dd>{o.compteDebite ? `${o.compteDebite.banque} · ${o.compteDebite.code}` : '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Compte crédité</dt>
                    <dd>{o.compteCredite ? `${o.compteCredite.banque} · ${o.compteCredite.code}` : '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Date souhaitée</dt>
                    <dd>{dateFr(nv.dateSouhaitee)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Exécution</dt>
                    <dd>{nv.dateExecution ? `${dateFr(nv.dateExecution)} · référence ${nv.referenceExecution ?? ''}` : 'non exécuté'}</dd>
                  </div>
                </>
              )}
            </dl>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Historique</CardTitle>
          </CardHeader>
          <CardContent>
            <HistoriqueListe historique={o.historique} />
          </CardContent>
        </Card>
      </div>
      {dialogue === 'decision' && <DialogueDecision o={o} ouvert onOpenChange={(x) => !x && setDialogue(null)} />}
      {dialogue === 'execution' && <DialogueExecution o={o} ouvert onOpenChange={(x) => !x && setDialogue(null)} />}
    </div>
  );
}
